import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileAlt,
  faCreditCard,
  faChartLine,
  faUserSecret,
  faUsers,
  faBell,
  faExclamationCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import { apiEndpoints } from "../services/api";
import "./DashboardPage.css";
import bgVideo from "../assets/dash1.jpg"

// --- IMPORT YOUR LOGO ---
// **IMPORTANT**: Create an 'assets' folder in your 'src' directory (src/assets)
// and place your logo image file there. Then, update the path and filename below.
// For example, if your logo is src/assets/rms-logo.png:
import companyLogo from '../assets/logo.jpg'; // <<< UPDATE THIS PATH AND FILENAME

const POLLING_INTERVAL = 15000;
const MAX_RECENT_ACTIVITIES = 7;

// --- localStorage Helper Functions ---
const STORAGE_KEY_PREFIX = "dashboard_toasted_ids_";

const getToastedIdsFromStorage = (dataKey) => {
  try {
    const item = localStorage.getItem(`${STORAGE_KEY_PREFIX}${dataKey}`);
    return item ? new Set(JSON.parse(item)) : new Set();
  } catch (e) {
    console.error(`Error reading toasted IDs for ${dataKey} from localStorage:`, e);
    return new Set();
  }
};

const setToastedIdsInStorage = (dataKey, idSet) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${dataKey}`, JSON.stringify(Array.from(idSet)));
  } catch (e) {
    console.error(`Error writing toasted IDs for ${dataKey} to localStorage:`, e);
  }
};
// --- End localStorage Helpers ---


// Reusable StatCard Component (remains the same)
const StatCard = ({ title, value, description, icon, iconClass, path, isLoading, navigate }) => {
  const handleClick = () => { if (path) navigate(path); };
  return (
    <div className="stat-card" onClick={path ? handleClick : undefined} style={path ? { cursor: 'pointer' } : {}}>
      <div className="stat-card-header">
        <h3 className="stat-card-title">{title}</h3>
        {isLoading ? <FontAwesomeIcon icon={faSpinner} spin className={`stat-card-icon ${iconClass || ''}`} /> : (icon && <FontAwesomeIcon icon={icon} className={`stat-card-icon ${iconClass || ''}`} />)}
      </div>
      {isLoading ? <p className="stat-card-value">...</p> : <p className="stat-card-value">{value}</p>}
      <p className="stat-card-description">{description}</p>
    </div>
  );
};

// Reusable ActivityItem Component (remains the same)
const ActivityItem = ({ icon, text, time, iconClass }) => (
  <li className="activity-item">
    {icon && <FontAwesomeIcon icon={icon} className={`activity-icon ${iconClass || ''}`} />}
    <span className="activity-text">{text}</span>
    <span className="activity-time">{time}</span>
  </li>
);


const DashboardPage = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    devices: 0, payments: 0, sales: 0, agents: 0, customers: 0, notifications: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [isLoading, setIsLoading] = useState({
    devices: true, payments: true, sales: true, agents: true, customers: true, notifications: true,
  });
  const [error, setError] = useState({});

  // Initialize refs from localStorage
  const toastedDeviceIds = useRef(getToastedIdsFromStorage('devices'));
  const toastedPaymentIds = useRef(getToastedIdsFromStorage('payments'));
  const toastedSaleIds = useRef(getToastedIdsFromStorage('sales'));
  const toastedAgentIds = useRef(getToastedIdsFromStorage('agents'));
  const toastedCustomerIds = useRef(getToastedIdsFromStorage('customers'));
  const toastedNotificationIds = useRef(getToastedIdsFromStorage('notifications'));

  const isInitialDataLoadCycle = useRef(true);


  const addActivity = useCallback((newActivity) => {
    setRecentActivities(prevActivities =>
      [newActivity, ...prevActivities].slice(0, MAX_RECENT_ACTIVITIES)
    );
  }, []);

  const fetchData = useCallback(async (
    endpointFunc,
    dataKey,
    toastedIdsRef,
    toastMessageFunc,
    activityIcon,
    activityIconClass,
    idField = 'id'
  ) => {
    if (isInitialDataLoadCycle.current) {
      setIsLoading(prev => ({ ...prev, [dataKey]: true }));
    }

    try {
      // ======================= START OF CORRECTION =======================
      // The only changes are inside this 'try' block.

      // 1. Get the authentication token from localStorage.
      const token = localStorage.getItem('authToken');

      // 2. If no token exists, the user should not be here. Redirect to login.
      if (!token) {
        toast.error("Authentication required. Redirecting to login.");
        navigate('/login');
        return; // Stop this function from continuing.
      }
      
      // 3. Create the headers object required for authenticated API calls.
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const url = endpointFunc();
      
      // 4. Add the 'headers' object to your fetch request.
      const response = await fetch(url, { headers });

      // 5. Handle a 401 Unauthorized error (e.g., expired token) from the server.
      if (response.status === 401) {
        toast.error("Your session has expired. Please log in again.");
        localStorage.removeItem('authToken'); // Clear the invalid token
        navigate('/login');
        return; // Stop this function from continuing.
      }
      
      // ======================== END OF CORRECTION ========================

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status} for ${dataKey}`;
        try { const errData = await response.json(); errorMsg = errData.message || errorMsg; } catch (e) { /* ignore */ }
        throw new Error(errorMsg);
      }
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        setStats(prevStats => ({ ...prevStats, [dataKey]: result.data.length }));

        const newItems = [];
        result.data.forEach(item => {
          const itemIdStr = item[idField]?.toString();
          if (!toastedIdsRef.current.has(itemIdStr)) {
            newItems.push(item);
          }
        });

        if (!isInitialDataLoadCycle.current && newItems.length > 0) {
          newItems.forEach(item => {
            const message = toastMessageFunc(item);
            if (dataKey === 'payments') toast.success(message);
            else toast.info(message);

            addActivity({
              id: `${dataKey}-${item[idField]?.toString()}`,
              icon: activityIcon, iconClass: activityIconClass, text: message,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            });
          });
        }

        const currentIdsFromFetch = new Set(result.data.map(item => item[idField]?.toString()));
        toastedIdsRef.current = currentIdsFromFetch;
        setToastedIdsInStorage(dataKey, currentIdsFromFetch);

      } else {
        throw new Error(result.message || `Failed to fetch ${dataKey}: API reported failure`);
      }
      setError(prevError => ({ ...prevError, [dataKey]: null }));
    } catch (err) {
      console.error(`Error fetching ${dataKey}:`, err.message);
      setError(prevError => ({ ...prevError, [dataKey]: `Failed to load ${dataKey}.` }));
    } finally {
      setIsLoading(prev => ({ ...prev, [dataKey]: false }));
    }
  }, [addActivity, navigate]); // Added 'navigate' because it is now used inside the useCallback.


  useEffect(() => {
    const fetchAllInitialData = () => {
      fetchData(apiEndpoints.getDevices, 'devices', toastedDeviceIds, item => `New device: ${item.device_name}`, faMobileAlt, 'icon-devices');
      fetchData(apiEndpoints.getPayments, 'payments', toastedPaymentIds, item => `New payment: $${item.amount}`, faCreditCard, 'icon-payments');
      fetchData(apiEndpoints.getSales, 'sales', toastedSaleIds, item => `New sale: ${item.sold_device_name}`, faChartLine, 'icon-sales');
      fetchData(apiEndpoints.getAgents, 'agents', toastedAgentIds, item => `New agent: ${item.name}`, faUserSecret, 'icon-agents', 'user_id');
      fetchData(apiEndpoints.getCustomers, 'customers', toastedCustomerIds, item => `New customer: ${item.name || (item.first_name + ' ' + item.last_name) || 'N/A'}`, faUsers, 'icon-customers');
      fetchData(apiEndpoints.getNotifications, 'notifications', toastedNotificationIds, item => `New notification: ${item.title || "Untitled"}`, faBell, 'icon-notifications');
    };

    fetchAllInitialData();
    isInitialDataLoadCycle.current = false;

    const intervalId = setInterval(fetchAllInitialData, POLLING_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    toast.success("Logged out successfully!");
    navigate("/login");
  };

  const overallLoading = Object.values(isLoading).some(status => status);
  const anyError = error && Object.values(error).some(message => message);

  return (
    <div className="dashboard-container">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop />
      <header className="dashboard-header">
        {/* --- LOGO AND COMPANY NAME ADDED --- */}
        <div className="dashboard-header-brand">
          {companyLogo && ( // Conditionally render if logo is imported
            <img 
              src={companyLogo} 
              alt="Reliable Mobile Service Logo" 
              className="dashboard-logo"
            />
          )}
          <div className="dashboard-header-titles">
            <h1>Admin Dashboard</h1>
            <span className="company-name">Reliable Mobile Service</span>
          </div>
        </div>
        {/* --- END LOGO AND COMPANY NAME --- */}
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </header>

      <main className="dashboard-main">
        {/* 🔹 Video background */}
      <video autoPlay loop muted playsInline className="dashboard-bg-video">
        <source src={bgVideo} type="video/mp4" />
      </video>


        <h2>Overview</h2>
        <p className="dashboard-welcome">Welcome back! Here's a summary of your operations.</p>

        {anyError && (
          <div className="error-text dashboard-error-banner"> {/* Added class for specific styling */}
            <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '8px' }} />
            Some data could not be loaded. Please check console or try refreshing.
            {error && Object.entries(error).map(([key, msg]) => msg ? <div key={key} className="error-item"> - {key}: {msg}</div> : null)}
          </div>
        )}

        <div className="stats-grid">
          <StatCard title="Total Devices" value={stats.devices} description="Manage all registered devices" icon={faMobileAlt} iconClass="icon-devices" path="/devices" isLoading={isLoading.devices} navigate={navigate} />
          <StatCard title="Total Payments" value={stats.payments} description="View and manage payments" icon={faCreditCard} iconClass="icon-payments" path="/payments" isLoading={isLoading.payments} navigate={navigate} />
          <StatCard title="Total Sales" value={stats.sales} description="Track sales performance" icon={faChartLine} iconClass="icon-sales" path="/sales" isLoading={isLoading.sales} navigate={navigate} />
          <StatCard title="Active Agents" value={stats.agents} description="Manage your agent network" icon={faUserSecret} iconClass="icon-agents" path="/agents/manage" isLoading={isLoading.agents} navigate={navigate} />
          <StatCard title="Customers" value={stats.customers} description="Manage customer profiles" icon={faUsers} iconClass="icon-customers" path="/customers" isLoading={isLoading.customers} navigate={navigate} />
          <StatCard title="Notifications" value={stats.notifications} description="View system notifications" icon={faBell} iconClass="icon-notifications" path="/notifications" isLoading={isLoading.notifications} navigate={navigate} />
        </div>

        <div className="recent-activity-section">
          <h3>Recent Activity</h3>
          {(isLoading.devices || isLoading.payments || isLoading.sales || isLoading.agents || isLoading.customers || isLoading.notifications) && recentActivities.length === 0 && <p className="loading-text">Loading activities...</p>}
          {!(isLoading.devices || isLoading.payments || isLoading.sales || isLoading.agents || isLoading.customers || isLoading.notifications) && recentActivities.length === 0 && <p>No new activity to display.</p>}
          <ul className="recent-activity-list">
            {recentActivities.map(activity => (
              <ActivityItem key={activity.id} icon={activity.icon} iconClass={activity.iconClass} text={activity.text} time={activity.time} />
            ))}
          </ul>
        </div>
      </main>
      <footer className="dashboard-footer"> {/* Added class for styling */}
        © {new Date().getFullYear()} Reliable Mobile Service. All rights reserved.
      </footer>
    </div>
  );
};

export default DashboardPage;